import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { MatchWithJob } from "@jobreel/shared";
import { openRazorpayCheckout, type RazorpaySuccess } from "../lib/razorpay";
import { useAuthStore } from "../store";

export interface Plan { id: string; credits: number; amount: number; currency: string; label: string; }
export interface PlansResponse { enabled: boolean; keyId: string | null; plans: Plan[]; }

/** Credit-pack catalog + whether checkout is live (drives the paywall UI). */
export function usePlans() {
  return useQuery<PlansResponse>({
    queryKey: ["payment-plans"],
    queryFn:  async () => (await axios.get("/api/payments/plans")).data,
  });
}

/** The user's paid deep-score credit balance. */
export function useCredits() {
  return useQuery<{ credits: number }>({
    queryKey: ["payment-credits"],
    queryFn:  async () => (await axios.get("/api/payments/credits")).data,
  });
}

export interface Purchase {
  id: string; planId: string; credits: number; amount: number; currency: string; createdAt: string;
}

/** The user's successful purchases (billing page history). */
export function usePaymentHistory() {
  return useQuery<Purchase[]>({
    queryKey: ["payment-history"],
    queryFn:  async () => (await axios.get("/api/payments/history")).data,
  });
}

/**
 * The full purchase flow: create a server order → open Razorpay Checkout →
 * verify the signature server-side → resolve with the new credit balance. The
 * amount is never touched here — the server order is authoritative.
 */
export function useBuyCredits() {
  const qc   = useQueryClient();
  const user = useAuthStore(s => s.user);
  return useMutation<{ credits: number }, unknown, string>({
    mutationFn: async (planId) => {
      const { data: order } = await axios.post("/api/payments/order", { planId });
      return new Promise<{ credits: number }>((resolve, reject) => {
        openRazorpayCheckout({
          keyId: order.keyId, orderId: order.orderId, amount: order.amount, currency: order.currency,
          name: "Caliber", description: `${order.credits} deep ${order.credits === 1 ? "analysis" : "analyses"}`,
          ...(user?.email ? { prefill: { email: user.email, name: user.name } } : {}),
          onSuccess: async (r: RazorpaySuccess) => {
            try {
              const { data } = await axios.post("/api/payments/verify", {
                razorpayOrderId:   r.razorpay_order_id,
                razorpayPaymentId: r.razorpay_payment_id,
                razorpaySignature: r.razorpay_signature,
              });
              resolve(data);
            } catch (e) { reject(e); }
          },
          onDismiss: () => reject(new Error("cancelled")),
        }).catch(reject);
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payment-credits"] });
      qc.invalidateQueries({ queryKey: ["payment-history"] });
    },
  });
}

/** Spend one paid credit to fully LLM-score a match. Returns the scored match + balance. */
export function useDeepScorePaid() {
  const qc = useQueryClient();
  return useMutation<{ match: MatchWithJob; credits: number }, unknown, string>({
    mutationFn: async (matchId) => (await axios.post(`/api/matches/${matchId}/deep-score`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payment-credits"] });
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["matches-page"] });
    },
  });
}
